import React, { useMemo } from 'react';

import { Button } from '@app/components/atoms';

import * as Styled from './styles';
import ErrorMessage from '@app/components/atoms/ErrorMessage';
import { Controller } from 'react-hook-form';

import PriceSlider from '@app/components/atoms/PriceSlider';
import Select from '@app/components/atoms/Select';
import { StylesConfig } from 'react-select';
import PaginationFilter from '@app/components/atoms/PaginationFilter';
import TypeFilter from '@app/components/atoms/TypeFilter';
import SearchCity from '../SearchCity';
import {
  GARAGE_SPOTS,
  LOT_MAX,
  LOT_MIN,
  MAX_SQFT,
  MIN_SQFT,
} from '@app/pages/buyerIntakeContainer/BuyerIntakeThird/data';
import MultiSelectList from '@app/components/atoms/MultiSelectList';
import SearchAddress from '../SearchAddress';
import IntakeProperty from '@app/components/atoms/IntakeProperty';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export const BEDS = [
  { id: 0, label: 'Any', value: null },
  { id: 1, label: 'Studio', value: 0 },
  { id: 2, label: '1+', value: 1 },
  { id: 3, label: '2+', value: 2 },
  { id: 4, label: '3+', value: 3 },
  { id: 5, label: '4+', value: 4 },
];

export const BATHS = [
  { id: 0, label: '1+', value: 1 },
  { id: 1, label: '1.5+', value: 1.5 },
  { id: 2, label: '2+', value: 2 },
  { id: 3, label: '3+', value: 3 },
  { id: 4, label: '4+', value: 4 },
];

export const HOME_TYPE = [
  {
    id: 0,
    icon: ['fas', 'home-alt'],
    label: 'House',
    value: 'house',
  },
  {
    id: 1,
    icon: ['fas', 'city'],
    label: 'Condo',
    value: 'condo',
  },
  {
    id: 2,
    icon: ['fas', 'building'],
    label: 'Townhouse',
    value: 'townhouse',
  },
  {
    id: 3,
    icon: ['fas', 'tree-alt'],
    label: 'Land',
    value: 'land',
  },
];

export const styles: StylesConfig = {
  option: ({ isDisabled, isFocused, isSelected }: any) => {
    return {
      backgroundColor: isDisabled
        ? '#929699'
        : isSelected
        ? undefined
        : isFocused
        ? '#D47E5B'
        : undefined,
      color: isDisabled
        ? '#929699'
        : isSelected
        ? undefined
        : isFocused
        ? '#D47E5B'
        : undefined,
    };
  },
  control: (base: any) => ({
    ...base,
    height: 50,
    boxShadow: 'none',
    borderColor: 'hsl(0, 0%, 80%)',
    '&:hover': {
      borderColor: 'hsl(0, 0%, 80%)',
    },
  }),
  placeholder: (provided, state: any) => {
    return {
      ...provided,
      position: 'absolute',
      top:
        state.hasValue || state.selectProps.inputValue
          ? -35
          : state.selectProps.isSearchable
          ? '20%'
          : '10%',
      transition: 'top 0.4s, font-size 0.1s',
      fontSize: 16,
      color: '#888888',
      fontWeight: 600,
    };
  },
  valueContainer: (provided, state: any) => {
    return {
      ...provided,
      overflow: 'hidden',
      padding: '2px 15px',
    };
  },
  dropdownIndicator: (styles) => {
    return {
      ...styles,
      paddingRight: 25,
      color: '#4f4f4f',
    };
  },
  container: (provided) => ({
    ...provided,
    fontSize: 18,
  }),
  singleValue: (provided) => ({
    ...provided,
    fontSize: 16,
    fontWeight: 600,
    color: '#4f4f4f',
  }),
  menu: (styles) => {
    return {
      ...styles,
      zIndex: 999,
    };
  },
};

/**
 * Client Property Form
 */
export interface ClientPropertyFormProps {
  register: any;
  control: any;
  clickCancel: () => void;
  loading?: boolean;
  update?: boolean;
  timings: any;
  highlights: any;
  error?: string;
  watch: any;
  setValue: any;
  clickSkip?: () => void;
  client: any;
}

const ClientPropertyForm = ({
  register,
  control,
  clickCancel,
  loading,
  update,
  timings,
  highlights,
  watch,
  setValue,
  error,
  clickSkip,
  client,
}: ClientPropertyFormProps) => {
  const snapValues = useMemo(() => {
    const min = 0;
    const step = 1000000;
    const max = 10000000;
    const interval = 25000;
    const secondInterval = 250000;

    const length = (step - min) / interval + 1;
    const lengthSecond = (max - (step + 250000)) / secondInterval + 1;

    const firstArray = Array.from({ length }, (_, i) => min + i * interval);
    const secondArray = Array.from(
      { length: lengthSecond },
      (_, i) => step + 250000 + i * secondInterval,
    );
    const finalArray = [...firstArray, ...secondArray];

    return finalArray;
  }, []);

  const handleSetHomeType = (oldArray: any, value: string) => {
    let array: any = [...oldArray];

    if (array.includes(value as string)) {
      const index = array.findIndex((item: any) => item === value);
      array.splice(index, 1);
    } else {
      array.push(value as string);
    }

    return array;
  };

  const el = document.getElementById('priceSliderWrapper')?.offsetWidth;

  const handleSelectAddress = (address: any, oldArray: any) => {
    let array: any = [...oldArray];
    array.push(address);

    return array;
  };

  const removeAddress = (idx: number) => {
    const array = [...watch('selectedAddress')];
    array.splice(idx, 1);

    setValue('selectedAddress', array);
  };

  return (
    <Styled.Container>
      <Styled.CloseContainer onClick={clickCancel}>
        <Styled.CloseIcon icon={['fas', 'times']} />
      </Styled.CloseContainer>

      <Styled.TitleWrapper row itemsCenter>
        <Styled.TitleIcon icon={['fas', 'sack-dollar']} />
        <Styled.Title variant="xl" color="#4F4F4F">
          {update ? 'Update' : 'Add'} buyer criteria
        </Styled.Title>
      </Styled.TitleWrapper>

      <Styled.SearchTitleWrapper row justifyBetween>
        <Styled.SearchTitleContainer>
          <Styled.SearchTitle variant="xl" color="#4F4F4F">
            Basic search criteria
          </Styled.SearchTitle>
          <Styled.SearchSubtitle variant="sm" color="#4F4F4F">
            Tell us about the homes you are interested in.
          </Styled.SearchSubtitle>
        </Styled.SearchTitleContainer>

        {clickSkip && (
          <Styled.SkipButton variant="sm" color="#D47E5B" onClick={clickSkip}>
            Skip
          </Styled.SkipButton>
        )}
      </Styled.SearchTitleWrapper>

      <Styled.PriceSliderWrapper id="priceSliderWrapper">
        <Controller
          control={control}
          name="priceRange"
          render={({ field: { onChange, value } }) => (
            <PriceSlider
              minVal={0}
              maxVal={snapValues.length - 1}
              selectedMin={value?.min}
              selectedMax={value?.max}
              maxSnapValue={snapValues[snapValues.length - 1]}
              snapValues={snapValues}
              setValues={(values: { min: number; max: number }) =>
                onChange(values)
              }
              hideInput={false}
              small={(el && el < 500) || false}
            />
          )}
        />
      </Styled.PriceSliderWrapper>

      <Styled.BedsBathsWrapper>
        <Controller
          control={control}
          name="beds"
          render={({ field: { onChange, value } }) => (
            <PaginationFilter
              data={BEDS}
              selectItem={(e) => {
                onChange(e);
              }}
              selectedItem={value?.value}
              filterName="Beds"
              required
            />
          )}
        />
        <Controller
          control={control}
          name="baths"
          render={({ field: { onChange, value } }) => (
            <PaginationFilter
              data={BATHS}
              selectItem={(e) => {
                onChange(e);
              }}
              selectedItem={value?.value}
              filterName="Baths"
              required
            />
          )}
        />
      </Styled.BedsBathsWrapper>

      <Styled.Label variant="sm" color="#888">
        Home type
      </Styled.Label>
      <Controller
        control={control}
        name="homeType"
        render={({ field: { onChange, value } }) => (
          <Styled.TypeFiltersWrapper>
            {HOME_TYPE.map((home) => (
              <TypeFilter
                key={home.id}
                icon={home.icon}
                label={home.label}
                selected={value.includes(home.value)}
                onClick={() => {
                  const newValues = handleSetHomeType(value, home.value);
                  onChange(newValues);
                }}
                hideIconMobile
              />
            ))}
          </Styled.TypeFiltersWrapper>
        )}
      />

      <Styled.Label variant="sm" color="#888">
        Location
      </Styled.Label>
      <Controller
        control={control}
        name="selectedCities"
        rules={{ required: 'City is required' }}
        render={({ field: { onChange, value } }) => (
          <Styled.SearchCityWrapper>
            <SearchCity
              setSelectedCities={onChange}
              selectedCities={value}
              searchLabel="Add US city"
              customStyles={styles}
              required
              selectedNeighorhoods={watch('selectedNeighorhoods')}
              setSelectedNeighorhoods={(value) =>
                setValue('selectedNeighorhoods', value)
              }
            />
          </Styled.SearchCityWrapper>
        )}
      />

      <Styled.Label variant="sm" color="#888">
        Preferred timing of your home purchase
      </Styled.Label>
      <Controller
        name="preferredTiming"
        control={control}
        render={({ field: { onChange, value } }) => (
          <Select
            maxMenuHeight={200}
            isClearable
            options={timings}
            value={value}
            onChange={onChange}
            placeholder="Preferred timing"
            customStyles={styles}
          />
        )}
      />

      <Styled.SectionTitle variant="xl" color="#4F4F4F">
        Additional criteria
      </Styled.SectionTitle>
      <Styled.SectionSubtitle variant="sm" color="#4F4F4F">
        Tell us about the homes you are interested in.
      </Styled.SectionSubtitle>

      <Styled.Label variant="sm" color="#888">
        Square feet
      </Styled.Label>
      <Styled.Row row itemsCenter>
        <Styled.InputContainer bigger>
          <Controller
            name="squareFeetMin"
            control={control}
            rules={{ required: 'Square feet minimum is required' }}
            render={({ field: { onChange, value } }: any) => (
              <Select
                maxMenuHeight={200}
                isClearable
                options={MIN_SQFT}
                value={value}
                onChange={(item: any) => {
                  if (
                    parseInt(item?.value) >=
                    parseInt((watch('squareFeetMax') as any)?.value || '0')
                  ) {
                    setValue('squareFeetMax', null);
                  }
                  onChange(item);
                }}
                placeholder="Min req"
                customStyles={styles}
                required
              />
            )}
          />
        </Styled.InputContainer>
        <Styled.InputContainer>
          <Controller
            name="squareFeetMax"
            control={control}
            render={({ field: { onChange, value } }: any) => (
              <Select
                maxMenuHeight={200}
                isClearable
                options={MAX_SQFT}
                value={value}
                onChange={(item: any) => {
                  if (
                    parseInt(item?.value) <=
                    parseInt((watch('squareFeetMin') as any)?.value || '0')
                  ) {
                    setValue('squareFeetMin', null);
                  }
                  onChange(item);
                }}
                placeholder="No max"
                customStyles={styles}
              />
            )}
          />
        </Styled.InputContainer>
      </Styled.Row>

      <Styled.Label variant="sm" color="#888888" hasMarginTop>
        Lot size
      </Styled.Label>
      <Styled.Row row itemsCenter>
        <Styled.InputContainer>
          <Controller
            name="lotSizeMin"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Select
                maxMenuHeight={200}
                isClearable
                options={LOT_MIN}
                value={value}
                onChange={(item: any) => {
                  if (
                    parseInt(item?.value) >=
                    parseInt((watch('lotSizeMax') as any)?.value || '0')
                  ) {
                    setValue('lotSizeMax', null);
                  }
                  onChange(item);
                }}
                placeholder="No min"
                customStyles={styles}
              />
            )}
          />
        </Styled.InputContainer>
        <Styled.InputContainer>
          <Controller
            name="lotSizeMax"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Select
                maxMenuHeight={200}
                isClearable
                options={LOT_MAX}
                value={value}
                onChange={(item: any) => {
                  if (
                    parseInt(item?.value) <=
                    parseInt((watch('lotSizeMin') as any)?.value || '0')
                  ) {
                    setValue('lotSizeMin', null);
                  }
                  onChange(item);
                }}
                placeholder="No max"
                customStyles={styles}
              />
            )}
          />
        </Styled.InputContainer>
      </Styled.Row>

      <Styled.GarageContainer>
        <Controller
          control={control}
          name="garageSpots"
          render={({ field: { onChange, value } }: any) => (
            <PaginationFilter
              data={GARAGE_SPOTS}
              selectItem={(e) => {
                onChange(e);
              }}
              selectedItem={value?.value}
              filterName="Garage spots"
            />
          )}
        />
      </Styled.GarageContainer>

      <Styled.SectionTitle variant="xl" color="#4F4F4F">
        Property highlights
      </Styled.SectionTitle>
      <Styled.SectionSubtitle variant="sm" color="#4F4F4F">
        Tell us more about what is the important property highlights in your
        home search.
      </Styled.SectionSubtitle>
      <Controller
        name="highlights"
        control={control}
        rules={{ required: 'Property highlights is required' }}
        render={({ field: { onChange, value } }) => (
          <MultiSelectList
            data={highlights}
            selectedItems={value}
            setSelectedItems={onChange}
          />
        )}
      />

      <Styled.SectionTitle variant="xl" color="#4F4F4F">
        Favorite homes
      </Styled.SectionTitle>
      <Styled.SectionSubtitle variant="sm" color="#4F4F4F">
        Add homes that are similar to your ideal home. We will use the
        attributes of these homes to find your dream home. We will add these
        home(s) to your favorite list.
      </Styled.SectionSubtitle>
      <Controller
        name="addressValue"
        control={control}
        render={({ field: { onChange, value } }) => (
          <SearchAddress
            searchLabel="Search address"
            required={false}
            selectedAddress={value}
            setSelectedAddress={(address) => {
              onChange('');
              const newValues = handleSelectAddress(
                address,
                watch('selectedAddress'),
              );
              setValue('selectedAddress', newValues);
            }}
            customStyles={styles}
          />
        )}
      />
      {watch('selectedAddress') && watch('selectedAddress')?.length > 0 && (
        <Styled.AddressesContainer>
          {watch('selectedAddress')?.map((item: any, idx: number) => (
            <IntakeProperty
              key={idx}
              clickRemove={() => removeAddress(idx)}
              selectedProperty={item}
            />
          ))}
        </Styled.AddressesContainer>
      )}

      <Styled.TextareaContainer>
        <Styled.InsideContainer row itemsCenter justifyBetween>
          <Styled.TextIconWrapper row itemsCenter>
            <Styled.InsideTitleIcon icon={['fas', 'memo']} />
            <Styled.InsideTitle variant="sm" color="#4F4F4F">
              Notes
            </Styled.InsideTitle>
          </Styled.TextIconWrapper>

          <Styled.TextIconWrapper row itemsCenter>
            <Styled.VisibleTextIcon icon={['fas', 'eye']} />
            <Styled.VisibleText variant="xs" color="#888888">
              Visible to other agents
            </Styled.VisibleText>
          </Styled.TextIconWrapper>
        </Styled.InsideContainer>

        <Styled.Textarea {...register('notes')} />
      </Styled.TextareaContainer>

      {client?.buyerCriterias?.[0]?.buyerGeneratedNote ? (
        <>
          <Styled.ClientNotesTitleWrapper row itemsCenter>
            <Styled.ClientNotesIcon icon={['fas', 'memo']} />
            <Styled.ClientNotesText variant="sm" color="#4F4F4F">
              Notes from client
            </Styled.ClientNotesText>
          </Styled.ClientNotesTitleWrapper>
          <div>{client?.buyerCriterias?.[0]?.buyerGeneratedNote}</div>
        </>
      ) : null}

      <Styled.ErrorWrapper>
        <ErrorMessage error={Boolean(error)} errorMsg={error} />
      </Styled.ErrorWrapper>

      {clickSkip && (
        <Styled.SkipContainer itemsEnd>
          <Styled.SkipButton variant="sm" color="#D47E5B" onClick={clickSkip}>
            Skip
          </Styled.SkipButton>
        </Styled.SkipContainer>
      )}

      <Styled.ButtonsContainer row itemsCenter justifyBetween>
        <Button variant="tertiary" onClick={clickCancel} disabled={loading}>
          Cancel
        </Button>
        <Button type="submit" disabled={loading} loading={loading}>
          {update ? 'Update' : 'Add'} criteria
        </Button>
      </Styled.ButtonsContainer>

      <Styled.RequiredWrapper itemsEnd>
        <Styled.RequiredText variant="sm" bold color="#4f4f4f">
          <span>
            <FontAwesomeIcon icon={['far', 'asterisk']} size="sm" />
          </span>{' '}
          Required
        </Styled.RequiredText>
      </Styled.RequiredWrapper>
    </Styled.Container>
  );
};

export default ClientPropertyForm;
